'use client'
import { useEffect, useRef } from 'react'
import type { CompanyAddressDTO, Option, OrderAddressDTO, OrderDTO } from 'ecosystem'
import { useUser } from 'auth'
import {
  findMatchingCompanyAddress,
  isSelectableAddress
} from '../components/CheckoutPayment/DefaultDelivery/companyAddressToOrderAddress'
import { type RememberedAddressChoice, useRememberedAddress } from './useRememberedAddress'

/**
 * Turn an address the order actually used into the choice worth remembering.
 *
 * When it is one of the company account's own addresses we store the id rather than a
 * copy, so the account stays the source of truth for its contents — the same rule the
 * picker follows. `undefined` for an address too incomplete to preselect later.
 */
const toChoice = <T extends OrderAddressDTO>(
  address: T | undefined,
  companyAddresses: CompanyAddressDTO[] | undefined
): RememberedAddressChoice<T> | undefined => {
  if (!address || !isSelectableAddress(address)) return undefined

  const match = findMatchingCompanyAddress(address, companyAddresses)

  return match ? { source: 'company', companyAddressId: match.id } : { source: 'custom', address }
}

/**
 * Remember the addresses a provisioned order actually shipped and billed to.
 *
 * Better memory than whatever was last typed into a form: a form can be edited after the
 * fact, or overridden by what the payment widget collected in its own iframe, while the
 * order records what was really used. Mount it on the confirmation page, where the
 * provisioned order is in hand.
 *
 * Pickup is skipped — the shipping address of a pickup order is the store's, not the
 * shopper's, and prefilling a shop counter into their next checkout would be plainly
 * wrong.
 *
 * Every `remember` here is a no-op unless the shopper ticked the box during checkout, so
 * this refreshes a permission they already gave rather than creating one behind their
 * back.
 */
export const useRememberProvisionedAddresses = (order?: Option<OrderDTO>) => {
  const { user } = useUser()
  const companyAddresses = user?.customer?.companyProfile?.addresses

  const billing = useRememberedAddress('billing')
  const shipping = useRememberedAddress('shipping')
  const postnord = useRememberedAddress('postnord')
  const nshift = useRememberedAddress('nshift')

  // One write per order. The confirmation page refetches the order, and without this
  // every revalidation would re-stamp the same addresses with a fresh 180-day expiry.
  const writtenForRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const orderId = order?.orderNumber ?? order?.id
    // `isReady` gates on the account having settled: writing before that has no identity
    // to file the address under, and would burn the once-per-order guard on a no-op.
    if (!order || !orderId || !billing.isReady || writtenForRef.current === orderId) return
    writtenForRef.current = orderId

    const additionalInfo = order.shippingInfo?.additionalInfo
    // The invoice flow records delivery on the shipment; the upfront flow records it on
    // the order and in `defaultDetails`. Reading only the latter is what made an invoice
    // order remember a billing address and nothing else.
    const shipment = order.shipments?.[0]
    const isPickup =
      order.shippingInfo?.shippingType === 'PICKUP' || shipment?.shippingType === 'PICKUP'

    const billingChoice = toChoice(order.billingAddress, companyAddresses)
    if (billingChoice) billing.remember(billingChoice)

    const shippingChoice = isPickup
      ? undefined
      : toChoice(
          order.shippingAddress ??
            additionalInfo?.defaultDetails?.shippingAddress ??
            shipment?.address,
          companyAddresses
        )
    if (shippingChoice) shipping.remember(shippingChoice)

    const postnordDetails = additionalInfo?.postnordDetails
    if (!postnordDetails?.isPickupFromStore && postnordDetails?.recipientAddress?.postCode) {
      postnord.remember(postnordDetails.recipientAddress)
    }

    const nshiftDetails = additionalInfo?.nshiftDetails
    if (!nshiftDetails?.isPickupFromStore && nshiftDetails?.recipientAddress?.postalCode) {
      nshift.remember(nshiftDetails.recipientAddress)
    }
    // The order (and the account it is written under) are the only signals. The four
    // `remember` callbacks change identity as consent resolves; depending on them would
    // re-enter this after the guard is already burnt.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [billing.isReady, companyAddresses, order])
}
